<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import api from '@/services/api'

// Import dinâmico: sweetalert2 (~80 KB) só é baixado ao submeter o formulário
const getSwal = () => import('sweetalert2').then(m => m.default)
import { useModalStore } from '@/stores/useModalStore'
import { usePhoneMask } from '@/composables/usePhoneMask'
import { useUtm } from '@/composables/useUtm'
import { pushEvent } from '@/composables/useGtm'
import { useAbTest } from '@/composables/useAbTest'
import { PRIVACY_POLICY_VERSION } from '@/constants/legal'

const { variant: abVariant, testKey: abTestKey } = useAbTest()

const modalStore = useModalStore()
const { formatPhone } = usePhoneMask()
const { getUtmParams } = useUtm()

const currentStep = ref(1)
const submitted = ref(false)
const isSubmitting = ref(false)

const steps = ['Dados', 'Data e hora', 'Confirmação']

const form = ref({
  nome: '',
  email: '',
  telefone: '',
  cargo: '',
  empresa: '',
  segmento: '',
  tamanho: '',
  data: null,
  horario: '',
  consentimento: false,
})

const errors = ref({
  nome: false,
  email: false,
  telefone: false,
  empresa: false,
  horario: false,
  consentimento: false,
})

// ── Calendário ──────────────────────────────────────────────────────────────
const today = new Date()
today.setHours(0, 0, 0, 0)
const calYear = ref(today.getFullYear())
const calMonth = ref(today.getMonth())

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

// JS getDay(): 0=Dom, 1=Seg, 2=Ter, 3=Qua, 4=Qui, 5=Sex, 6=Sáb
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

const calMonthLabel = computed(() => `${MONTHS[calMonth.value]} ${calYear.value}`)
const calFirstDay = computed(() => new Date(calYear.value, calMonth.value, 1).getDay())
const calDaysInMonth = computed(() => new Date(calYear.value, calMonth.value + 1, 0).getDate())

// ── Horários via API ─────────────────────────────────────────────────────────
// schedule: estrutura geral por dia da semana (para desabilitar dias no calendário)
const schedule = ref(null)
const isLoadingSchedule = ref(false)

async function fetchSchedule() {
  if (schedule.value || isLoadingSchedule.value) return
  isLoadingSchedule.value = true
  try {
    const res = await api.get('/public/demo-schedule')
    schedule.value = res.data?.data ?? null
  } catch {
    schedule.value = null
  } finally {
    isLoadingSchedule.value = false
  }
}

// dateSlots: slots disponíveis para a data selecionada (verificando conflitos reais)
const dateSlots = ref(null)
const isFetchingDateSlots = ref(false)

function formatDateParam(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

async function fetchSlotsForDate(date) {
  dateSlots.value = null
  isFetchingDateSlots.value = true
  try {
    const res = await api.get('/public/demo-schedule', { params: { date: formatDateParam(date) } })
    dateSlots.value = res.data?.data ?? []
  } catch {
    dateSlots.value = []
  } finally {
    isFetchingDateSlots.value = false
  }
}

function dayKey(date) {
  return DAY_KEYS[date.getDay()]
}

function isDayEnabled(date) {
  if (!schedule.value) return true // fallback: não bloqueia se ainda não carregou
  const key = dayKey(date)
  return schedule.value[key]?.enabled === true
}

const isDayDisabled = (day) => {
  const d = new Date(calYear.value, calMonth.value, day)
  if (d < today) return true
  const dow = d.getDay()
  if (dow === 0 || dow === 6) {
    // Fim de semana só aparece disponível se a agenda marcar como habilitado
    if (!schedule.value) return true
  }
  return !isDayEnabled(d)
}

const isToday = (day) => {
  const d = new Date(calYear.value, calMonth.value, day)
  return d.getTime() === today.getTime()
}

const isSelected = (day) => {
  if (!form.value.data) return false
  const d = new Date(calYear.value, calMonth.value, day)
  return d.getTime() === form.value.data.getTime()
}

const getDayClass = (day) => {
  if (isDayDisabled(day)) return 'text-gray-300 cursor-not-allowed'
  if (isSelected(day)) return 'bg-[#004d6b] text-white'
  if (isToday(day)) return 'border border-[#004d6b] text-[#004d6b] hover:bg-[#e6f3f7]'
  return 'text-gray-700 hover:bg-[#e6f3f7] hover:text-[#004d6b]'
}

const selectDate = (day) => {
  if (isDayDisabled(day)) return
  form.value.data = new Date(calYear.value, calMonth.value, day)
  form.value.horario = ''
  fetchSlotsForDate(form.value.data)
}

const changeMonth = (dir) => {
  let m = calMonth.value + dir
  let y = calYear.value
  if (m < 0) { m = 11; y-- }
  if (m > 11) { m = 0; y++ }
  const minY = today.getFullYear()
  const minM = today.getMonth()
  if (y < minY || (y === minY && m < minM)) return
  calMonth.value = m
  calYear.value = y
}

// Slots para a data selecionada — vindos da verificação real de conflitos
const availableTimes = computed(() => dateSlots.value ?? [])

// Alguns <option> do campo Segmento usam como value o slug do catálogo de
// leads (ver LeadCatalog::segmentValues() no backend), pra garantir que o
// lead caia no segmento certo no CRM. Esse mapa traduz o slug de volta pra
// um rótulo amigável na hora de exibir/enviar (resumo e mensagem do lead).
const SEGMENTO_LABELS = {
  agronegocio: 'Agronegócio / Produtor rural',
  saude: 'Clínica médica / Odontológica',
  restaurantes: 'Restaurante / Food service',
  pet_shop: 'Petshop / Clínica veterinária',
  beleza_estetica: 'Salão de beleza / Estúdio',
  ecommerce: 'Loja virtual / E-commerce',
}
const segmentoLabel = computed(() => SEGMENTO_LABELS[form.value.segmento] || form.value.segmento)

// ── Resumo ───────────────────────────────────────────────────────────────────
const summaryRows = computed(() => {
  const dateStr = form.value.data
    ? form.value.data.toLocaleDateString('pt-BR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      })
    : '—'
  return [
    { label: 'Nome', value: form.value.nome },
    { label: 'E-mail', value: form.value.email },
    { label: 'Telefone', value: form.value.telefone },
    { label: 'Empresa', value: form.value.empresa },
    form.value.cargo ? { label: 'Cargo', value: form.value.cargo } : null,
    form.value.segmento ? { label: 'Segmento', value: segmentoLabel.value } : null,
    { label: 'Data', value: dateStr },
    { label: 'Horário', value: `${form.value.horario} (horário de Brasília)` },
  ].filter(Boolean)
})

// ── Validação ────────────────────────────────────────────────────────────────
const validateStep1 = () => {
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)
  errors.value.nome = !form.value.nome.trim()
  errors.value.email = !emailOk
  errors.value.telefone = !form.value.telefone.trim()
  errors.value.empresa = !form.value.empresa.trim()
  errors.value.consentimento = !form.value.consentimento
  return !errors.value.nome && !errors.value.email && !errors.value.telefone && !errors.value.empresa && !errors.value.consentimento
}

const validateStep2 = () => {
  errors.value.horario = !form.value.horario
  return !!form.value.data && !!form.value.horario
}

const maskPhone = (e) => {
  form.value.telefone = formatPhone(e.target.value)
}

// ── Submit ───────────────────────────────────────────────────────────────────
const submitLead = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true

  const dateStr = form.value.data
    ? form.value.data.toLocaleDateString('pt-BR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      })
    : ''

  const { utm_source, utm_medium, utm_campaign } = getUtmParams({ utm_campaign: 'demo' })

  const messageLines = [
    'Solicitação de demonstração (Agendar Demo)',
    `Data: ${dateStr || '—'}`,
    `Horário: ${form.value.horario || '—'} (horário de Brasília)`,
    form.value.cargo ? `Cargo: ${form.value.cargo}` : null,
    form.value.segmento ? `Segmento: ${segmentoLabel.value}` : null,
    form.value.tamanho ? `Equipe: ${form.value.tamanho}` : null,
  ].filter(Boolean)

  const payload = {
    confirm_email: hpConfirmEmail.value,
    form_ts: formTs.value || undefined,
    name: form.value.nome,
    email: form.value.email,
    phone: form.value.telefone.replace(/\D/g, '').slice(0, 11) || form.value.telefone,
    message: messageLines.join('\n'),
    source: 'demo',
    page_url: window.location.href,
    utm_source,
    utm_medium,
    utm_campaign,
    company: form.value.empresa || null,
    pipeline: 'novo',
    project_type: null,
    reason: null,
    opportunity_type: null,
    seller: null,
    city: null,
    uf: null,
    municipio_uf: null,
    lgpd: {
      accepted: form.value.consentimento,
      policy_version: PRIVACY_POLICY_VERSION,
      text: 'Li e concordo com a Política de Privacidade da Kali Sistemas para o tratamento dos meus dados neste contato.',
    },
    payload: {
      source_cta: 'demo-modal',
      ab_test: abTestKey.value ? { key: abTestKey.value, variant: abVariant.value } : undefined,
      cargo: form.value.cargo || null,
      segmento: form.value.segmento || null,
      tamanho: form.value.tamanho || null,
      data: form.value.data ? form.value.data.toISOString() : null,
      horario: form.value.horario || null,
    },
  }

  const Swal = await getSwal()
  try {
    Swal.fire({
      title: 'Enviando...',
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    })
    await api.post('/public/lead', payload)
    Swal.close()
    submitted.value = true
    pushEvent('lead_demo', {
      source_page: window.location.pathname,
      segmento: form.value.segmento || undefined,
    })
  } catch {
    Swal.close()
    Swal.fire({
      title: 'Falha ao enviar',
      text: 'Tente novamente mais tarde.',
      icon: 'error',
      iconColor: '#ef4444',
      toast: true,
      position: 'bottom-end',
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
    })
  } finally {
    isSubmitting.value = false
  }
}

const nextStep = () => {
  if (currentStep.value === 1 && !validateStep1()) return
  if (currentStep.value === 2 && !validateStep2()) return
  if (currentStep.value === 3) { submitLead(); return }
  currentStep.value++
}

const resetModal = () => {
  currentStep.value = 1
  submitted.value = false
  isSubmitting.value = false
  form.value = {
    nome: '', email: '', telefone: '', cargo: '',
    empresa: '', segmento: '', tamanho: '', data: null, horario: '',
    consentimento: false,
  }
  errors.value = { nome: false, email: false, telefone: false, empresa: false, horario: false, consentimento: false }
  calMonth.value = today.getMonth()
  calYear.value = today.getFullYear()
  dateSlots.value = null
}

const close = () => {
  modalStore.closeDemo()
  setTimeout(resetModal, 300)
}

const handleEsc = (e) => {
  if (e.key === 'Escape' && modalStore.showDemo) close()
}

// Anti-spam: honeypot + timestamp de quando o modal abriu.
const hpConfirmEmail = ref('')
const formTs = ref(0)

watch(() => modalStore.showDemo, (open) => {
  if (open) {
    window.addEventListener('keydown', handleEsc)
    fetchSchedule()
    formTs.value = Math.floor(Date.now() / 1000)
    hpConfirmEmail.value = ''
    if (modalStore.demoSegmento) form.value.segmento = modalStore.demoSegmento
  } else {
    window.removeEventListener('keydown', handleEsc)
  }
})

onUnmounted(() => window.removeEventListener('keydown', handleEsc))
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="modalStore.showDemo"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        @click.self="close"
      >
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="close" />

        <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl lg:max-w-4xl overflow-hidden z-10">
          <div aria-hidden="true" style="position: absolute; left: -9999px; top: -9999px; width: 0; height: 0; overflow: hidden">
            <input v-model="hpConfirmEmail" type="text" name="confirm_email" tabindex="-1" autocomplete="off" />
          </div>
          <div class="bg-[#004d6b] px-6 py-5 flex items-start justify-between">
            <div>
              <h2 class="text-white text-xl font-bold">Agendar Demonstração</h2>
              <p class="text-white/75 text-sm mt-0.5">Preencha os dados e escolha seu horário</p>
            </div>
            <button @click="close" class="text-white/70 hover:text-white transition-colors ml-4 mt-0.5" aria-label="Fechar">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="flex border-b border-gray-100">
            <div
              v-for="(s, i) in steps"
              :key="i"
              class="flex-1 py-2.5 text-center text-xs font-medium transition-colors"
              :class="
                currentStep === i + 1
                  ? 'bg-[#e6f3f7] text-[#004d6b] border-b-2 border-[#004d6b]'
                  : currentStep > i + 1
                    ? 'bg-[#004d6b] text-white'
                    : 'text-gray-400'
              "
            >
              <span v-if="currentStep > i + 1">✓</span>
              <span v-else>{{ i + 1 }}</span>
              <span class="ml-1 hidden sm:inline">{{ s }}</span>
            </div>
          </div>

          <div class="px-6 py-5 overflow-y-auto max-h-[70vh] lg:max-h-[75vh]">
            <!-- Step 1: Dados pessoais -->
            <div v-if="currentStep === 1" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" for="demo_nome">Nome completo *</label>
                  <input
                    id="demo_nome"
                    v-model="form.nome" type="text" placeholder="Seu nome"
                    class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/30 focus:border-[#004d6b] transition"
                    :class="errors.nome ? 'border-red-400' : 'border-gray-300'"
                  />
                  <p v-if="errors.nome" class="text-red-500 text-xs mt-1">Campo obrigatório</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" for="demo_email">E-mail *</label>
                  <input
                    id="demo_email"
                    v-model="form.email" type="email" placeholder="Seu e-mail"
                    class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/30 focus:border-[#004d6b] transition"
                    :class="errors.email ? 'border-red-400' : 'border-gray-300'"
                  />
                  <p v-if="errors.email" class="text-red-500 text-xs mt-1">E-mail inválido</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" for="demo_telefone">Telefone / WhatsApp *</label>
                  <input
                    id="demo_telefone"
                    v-model="form.telefone" type="tel" placeholder="Seu telefone" @input="maskPhone"
                    class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/30 focus:border-[#004d6b] transition"
                    :class="errors.telefone ? 'border-red-400' : 'border-gray-300'"
                  />
                  <p v-if="errors.telefone" class="text-red-500 text-xs mt-1">Campo obrigatório</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" for="demo_cargo">Cargo</label>
                  <select id="demo_cargo" v-model="form.cargo" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/30 focus:border-[#004d6b] transition bg-white">
                    <option value="">Selecione...</option>
                    <option>CEO / Diretor</option>
                    <option>Gerente</option>
                    <option>Analista</option>
                    <option>Desenvolvedor</option>
                    <option>Outro</option>
                  </select>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1" for="demo_empresa">Empresa *</label>
                <input
                  id="demo_empresa"
                  v-model="form.empresa" type="text" placeholder="Nome da sua empresa"
                  class="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/30 focus:border-[#004d6b] transition"
                  :class="errors.empresa ? 'border-red-400' : 'border-gray-300'"
                />
                <p v-if="errors.empresa" class="text-red-500 text-xs mt-1">Campo obrigatório</p>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" for="demo_segmento">Segmento</label>
                  <select id="demo_segmento" v-model="form.segmento" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/30 focus:border-[#004d6b] transition bg-white">
                    <option value="">Selecione...</option>
                    <option>Indústrias</option>
                    <option>Prestadores de Serviços</option>
                    <option value="agronegocio">Agronegócio / Produtor rural</option>
                    <option value="saude">Clínica médica / Odontológica</option>
                    <option value="restaurantes">Restaurante / Food service</option>
                    <option value="pet_shop">Petshop / Clínica veterinária</option>
                    <option value="beleza_estetica">Salão de beleza / Estúdio</option>
                    <option value="ecommerce">Loja virtual / E-commerce</option>
                    <option>Outro</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1" for="demo_tamanho">Tamanho da equipe</label>
                  <select id="demo_tamanho" v-model="form.tamanho" class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004d6b]/30 focus:border-[#004d6b] transition bg-white">
                    <option value="">Selecione...</option>
                    <option>1–10 pessoas</option>
                    <option>11–50 pessoas</option>
                    <option>51–200 pessoas</option>
                    <option>200+ pessoas</option>
                  </select>
                </div>
              </div>
              <div>
                <label class="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox" v-model="form.consentimento"
                    class="mt-0.5 text-kali-blue-500 focus:ring-kali-blue-500 w-4 h-4 shrink-0"
                  />
                  <span class="text-xs text-gray-600 leading-relaxed">
                    Li e concordo com a
                    <a href="/politica-de-privacidade" target="_blank" class="text-[#006086] font-semibold hover:underline">Política de Privacidade</a>
                    da Kali Sistemas para o tratamento dos meus dados neste contato. *
                  </span>
                </label>
                <p v-if="errors.consentimento" class="text-red-500 text-xs mt-1">É necessário aceitar a Política de Privacidade para continuar</p>
              </div>
            </div>

            <!-- Step 2: Data e hora -->
            <div v-if="currentStep === 2" class="space-y-4">
              <div>
                <div class="flex items-center justify-between mb-3">
                  <button @click="changeMonth(-1)" class="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition text-gray-500">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span class="text-sm font-semibold text-gray-700">{{ calMonthLabel }}</span>
                  <button @click="changeMonth(1)" class="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition text-gray-500">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div class="grid grid-cols-7 gap-1 text-center">
                  <div v-for="d in ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']" :key="d" class="text-xs text-gray-400 font-medium py-1">{{ d }}</div>
                  <div v-for="_ in calFirstDay" :key="'e' + _" />
                  <button
                    v-for="day in calDaysInMonth" :key="day"
                    @click="selectDate(day)" :disabled="isDayDisabled(day)"
                    class="py-1.5 text-sm rounded-lg transition font-medium"
                    :class="getDayClass(day)"
                  >{{ day }}</button>
                </div>
              </div>

              <!-- Horários -->
              <div v-if="form.data">
                <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Horários disponíveis</p>

                <!-- Carregando slots da data -->
                <div v-if="isFetchingDateSlots" class="flex items-center justify-center py-6 text-gray-400 text-sm gap-2">
                  <div class="h-4 w-4 rounded-full border-2 border-gray-300 border-t-[#004d6b] animate-spin" />
                  Carregando horários...
                </div>

                <!-- Nenhum horário disponível neste dia -->
                <p v-else-if="dateSlots !== null && availableTimes.length === 0" class="text-sm text-gray-400 text-center py-2">
                  Nenhum horário disponível neste dia
                </p>

                <!-- Grade de horários -->
                <div v-else-if="availableTimes.length > 0" class="grid grid-cols-4 gap-2">
                  <button
                    v-for="t in availableTimes" :key="t"
                    @click="form.horario = t"
                    class="py-2 text-xs rounded-lg border transition font-medium"
                    :class="
                      form.horario === t
                        ? 'border-[#004d6b] bg-[#004d6b] text-white'
                        : 'border-gray-200 text-gray-600 hover:border-[#004d6b] hover:text-[#004d6b]'
                    "
                  >{{ t }}</button>
                </div>
                <p v-if="errors.horario" class="text-red-500 text-xs mt-2">Selecione um horário</p>
              </div>
              <p v-else class="text-sm text-gray-400 text-center py-2">Selecione uma data para ver os horários</p>
            </div>

            <!-- Step 3: Confirmação -->
            <div v-if="currentStep === 3 && !submitted" class="space-y-3">
              <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Resumo do agendamento</p>
              <div class="bg-gray-50 rounded-xl border border-gray-100 divide-y divide-gray-100">
                <div v-for="(row, i) in summaryRows" :key="i" class="flex justify-between px-4 py-2.5 text-sm">
                  <span class="text-gray-500">{{ row.label }}</span>
                  <span class="font-medium text-gray-800 text-right max-w-[55%]">{{ row.value }}</span>
                </div>
              </div>
              <p class="text-xs text-gray-400 leading-relaxed">
                Você receberá um e-mail de confirmação com o link da reunião. Em caso de dúvidas, entre em contato pelo WhatsApp.
              </p>
            </div>

            <!-- Sucesso -->
            <div v-if="submitted" class="flex flex-col items-center justify-center py-6 text-center space-y-3">
              <div class="w-16 h-16 rounded-full bg-[#e6f3f7] flex items-center justify-center">
                <svg class="w-8 h-8 text-[#004d6b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 class="text-lg font-bold text-gray-800">Demonstração agendada!</h3>
              <p class="text-sm text-gray-500 leading-relaxed">
                Seu horário foi reservado com sucesso.<br />
                Verifique seu e-mail para os detalhes e o link da reunião.
              </p>
              <button @click="close" class="mt-2 px-6 py-2 bg-[#004d6b] text-white text-sm rounded-lg hover:bg-[#003d55] transition font-medium">
                Fechar
              </button>
            </div>
          </div>

          <div v-if="!submitted" class="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
            <button v-if="currentStep > 1" @click="currentStep--" class="px-5 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-white transition">
              ← Voltar
            </button>
            <div v-else />
            <button
              @click="nextStep" :disabled="isSubmitting"
              class="px-6 py-2 text-sm bg-[#004d6b] text-white rounded-lg hover:bg-[#003d55] transition font-medium flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#004d6b]"
            >
              {{ isSubmitting ? 'Enviando...' : currentStep === 3 ? 'Confirmar agendamento' : 'Próximo →' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Alguns navegadores (Chrome/Edge no Windows) usam a cor de destaque do
   sistema para o item selecionado/em foco do <select>. Forçamos a cor da
   marca aqui — funciona nos navegadores baseados em Chromium; em outros,
   a lista cai no destaque padrão do sistema sem quebrar nada. */
select#demo_segmento option:checked,
select#demo_segmento option:hover {
  background-color: var(--color-kali-blue-500);
  color: #fff;
}
</style>

